/**
 * Computes the bounding box (extent) for a GeoJSON object.
 * The returned extent is in the format: [minLon, minLat, maxLon, maxLat]
 *
 * @param {Object} geojson - A valid GeoJSON object.
 * @returns {Array|null} The bounding box as an array or null if no coordinates found.
 */
export default function (geojson) {
	// Initialize extent with extreme values.
	const extent = [Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY];

	/**
	 * Recursively process coordinates.
	 * If the coordinates represent a point [lon, lat],
	 * update the extent; otherwise, drill into the array.
	 *
	 * @param {Array} coordinates - A coordinate or an array of coordinates.
	 */
	function processCoordinates(coordinates) {
		// If the first element is a number, assume it's a coordinate pair.
		if (typeof coordinates[0] === 'number') {
			const [lon, lat] = coordinates;
			extent[0] = Math.min(extent[0], lon);
			extent[1] = Math.min(extent[1], lat);
			extent[2] = Math.max(extent[2], lon);
			extent[3] = Math.max(extent[3], lat);
		} else if (Array.isArray(coordinates)) {
			// Recursively process each element.
			for (const coord of coordinates) {
				processCoordinates(coord);
			}
		}
	}

	/**
	 * Recursively process the GeoJSON object.
	 *
	 * @param {Object} obj - A GeoJSON geometry, feature, or collection.
	 */
	function processGeojson(obj) {
		if (!obj) return;
		const { type } = obj;
		switch (type) {
			case 'Feature':
				// Process the geometry of the feature.
				processGeojson(obj.geometry);
				break;

			case 'FeatureCollection':
				// Process each feature in the collection.
				if (Array.isArray(obj.features)) {
					obj.features.forEach((feature) => processGeojson(feature));
				}
				break;

			case 'GeometryCollection':
				// Process each geometry in the collection.
				if (Array.isArray(obj.geometries)) {
					obj.geometries.forEach((geometry) => processGeojson(geometry));
				}
				break;

			case 'Point':
			case 'MultiPoint':
			case 'LineString':
			case 'MultiLineString':
			case 'Polygon':
			case 'MultiPolygon':
				// Process the coordinates of the geometry.
				processCoordinates(obj.coordinates);
				break;

			default:
				throw new Error(`Unsupported GeoJSON type: ${type}`);
		}
	}

	// Start processing from the root GeoJSON object.
	processGeojson(geojson);

	// If no coordinates were found, return null.
	if (extent[0] === Number.POSITIVE_INFINITY) return null;
	return extent;
}